/**
 * Claude, through the Messages API.
 *
 * Called straight from the browser, which the API refuses by default — the
 * `anthropic-dangerous-direct-browser-access` header is Anthropic's way of
 * making you say out loud that you know the key is sitting in a browser. It is
 * the right trade for a family prototype with no server and the wrong one in
 * production; `proxy.ts` is the same assistant with the key somewhere safe.
 */
import type { AskReply, AskRequest, AssistantProvider, ToolCall, ToolDef, Turn } from './types';
import { httpError, networkError } from './types';

const API = 'https://api.anthropic.com/v1/messages';
const VERSION = '2023-06-01';

interface Block {
  type: string;
  text?: string;
  id?: string;
  name?: string;
  input?: Record<string, unknown>;
  tool_use_id?: string;
  content?: string;
  is_error?: boolean;
}

function toMessages(turns: Turn[]): Array<{ role: 'user' | 'assistant'; content: Block[] }> {
  const out: Array<{ role: 'user' | 'assistant'; content: Block[] }> = [];
  for (const turn of turns) {
    const content: Block[] = [];
    // Tool results are a *user* turn in this API, even though the app produced them.
    if (turn.results?.length) {
      for (const r of turn.results) content.push({ type: 'tool_result', tool_use_id: r.id, content: r.output, is_error: r.isError });
    }
    if (turn.text) content.push({ type: 'text', text: turn.text });
    if (turn.calls?.length) {
      for (const c of turn.calls) content.push({ type: 'tool_use', id: c.id, name: c.name, input: c.input });
    }
    if (!content.length) continue;
    out.push({ role: turn.role, content });
  }
  return out;
}

function toTools(tools: ToolDef[]) {
  return tools.map((t) => ({ name: t.name, description: t.description, input_schema: t.schema }));
}

export function createAnthropicProvider(opts: { apiKey: string; model: string; baseUrl?: string }): AssistantProvider {
  return {
    id: 'anthropic',
    label: 'Claude',
    ready: () => !!opts.apiKey.trim(),
    notReadyKey: () => (opts.apiKey.trim() ? null : 'assistant.error.noKey'),
    async ask({ system, turns, tools, signal }: AskRequest): Promise<AskReply> {
      let res: Response;
      try {
        res = await fetch(opts.baseUrl?.trim() || API, {
          method: 'POST',
          signal,
          headers: {
            'content-type': 'application/json',
            'x-api-key': opts.apiKey.trim(),
            'anthropic-version': VERSION,
            // Says out loud that this key is in a browser. See the note above.
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: opts.model,
            max_tokens: 700,
            system,
            messages: toMessages(turns),
            tools: toTools(tools),
          }),
        });
      } catch (e) {
        throw networkError(e);
      }
      if (!res.ok) throw await httpError(res);

      const data = (await res.json()) as { content?: Block[] };
      let text = '';
      const calls: ToolCall[] = [];
      for (const block of data.content ?? []) {
        if (block.type === 'text' && block.text) text += block.text;
        if (block.type === 'tool_use' && block.id && block.name) calls.push({ id: block.id, name: block.name, input: block.input ?? {} });
      }
      return { text: text.trim(), calls };
    },
  };
}
